import { useCallback } from "react";

import { Clear } from "@material-ui/icons";
import { Button } from "antd";
import styled from "styled-components";
import getColumnLabelWithContext from "utils/column/getColumnLabelWithContext";

import { IGroupBy, IGroupByListColumn, IGroupByMap } from "models";

import { BaseButton } from "components/base";
import GroupBy from "components/groupBy";
import {
  geoVolumeCategoryTitle,
  padMetricsCategoryTitle
} from "components/groupBy/hooks/useCategories";

type IGroupByWithMetadata = IGroupBy & {
  metadata?: IGroupByListColumn["metadata"];
};

interface XdaDataPickerSelectorProps {
  groupByValue: IGroupByMap;
  setGroupBy: (params: { title: string; property: string }) => void;
}

export function XdaDataPickerSelector({
  groupByValue,
  setGroupBy
}: XdaDataPickerSelectorProps) {
  const { title } = groupByValue;

  const handleFieldChange = useCallback(
    (field: IGroupBy) => {
      let fieldTitle = field.title;
      const fieldWithMetadata = field as IGroupByWithMetadata;
      const needsContextLabel =
        field.group === geoVolumeCategoryTitle ||
        field.group === padMetricsCategoryTitle ||
        field.property.startsWith("My_Focus_Fields") ||
        field.property.startsWith("Org_Focus_Fields") ||
        field.property.startsWith("Project_Focus_Fields");

      if (needsContextLabel) {
        const columnForContext: Pick<
          IGroupByListColumn,
          "group" | "title" | "property" | "metadata" | "subgroup" | "product"
        > = {
          group: fieldWithMetadata.group ?? "",
          title: fieldWithMetadata.title,
          property: fieldWithMetadata.property,
          metadata: fieldWithMetadata.metadata,
          subgroup: fieldWithMetadata.subgroup ?? "",
          product: fieldWithMetadata.product ?? ""
        };

        const contextLabels = getColumnLabelWithContext(
          columnForContext as IGroupByListColumn,
          false,
          null,
          false,
          ""
        );
        if (contextLabels.fullContextTitle) {
          fieldTitle = contextLabels.fullContextTitle;
        }
      }
      setGroupBy({ title: fieldTitle, property: field.property });
    },
    [setGroupBy]
  );

  const clearButton = title ? (
    <StyledXdaClearButton
      type="link"
      title="Clear"
      onClick={(evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        setGroupBy({ title: undefined, property: "None.Right" });
      }}
      icon={<Clear className="clear-icon" />}
    />
  ) : null;

  return (
    <ButtonWrapper>
      <GroupBy onChange={handleFieldChange} value={groupByValue}>
        <BaseButton iconAfter={clearButton}>{title ?? "Select Field"}</BaseButton>
      </GroupBy>
    </ButtonWrapper>
  );
}

const StyledXdaClearButton = styled(Button)`
  margin-top: 3px;
  width: 100%;
`;

const ButtonWrapper = styled.div`
  &:hover {
    .clear-icon {
      color: white;
    }
  }

  ${StyledXdaClearButton}:hover .clear-icon {
    color: black;
  }
`;
